import { useState, useEffect, useMemo, useRef } from "react";
import { database, functions } from "./firebase";
import {
  ref,
  get,
  set,
  query,
  orderByChild,
  startAt,
  endAt,
  equalTo,
  onValue,
  off,
  limitToFirst,
  orderByKey,
  runTransaction,
} from "firebase/database";
import { collection, getDocs, getFirestore } from "firebase/firestore";
import { useTransactionContext } from "../contexts/TransactionContext";
import * as firestoreAdapter from "../utils/firestoreAdapter";
import { getTransactionDescription } from "../utils/transactionDescription";

const db = getFirestore();

/**
 * 📊 Bandwidth & Size Estimator
 * Helps identify heavy downloads in console
 */

const estimateDataSize = (data, label = "Data") => {
  try {
    const jsonString = JSON.stringify(data);
    const bytes = new TextEncoder().encode(jsonString).length;
    const mb = (bytes / (1024 * 1024)).toFixed(2);

    // Flag heavy downloads (> 1MB)
    if (mb > 1.0) {
      console.log(`🚨 [High Bandwidth] ${label}: ${mb} MB downloaded!`);
    } else {
      const kb = (bytes / 1024).toFixed(2);
      console.log(`📉 [Bandwidth] ${label}: ${kb} KB`);
    }
    return bytes;
  } catch (e) {
    return 0;
  }
};

const parseComparableDate = (value) => {
  if (!value) return null;
  const normalized = String(value).trim().replace(/\//g, "-").split("T")[0];
  if (!normalized) return null;
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * Data Loading Utility for Backward Compatible Transaction Storage
 *
 * This utility handles both the old year-based storage system and the new unified storage system.
 * It automatically detects which system a company is using and loads data accordingly.
 */

// Debug mode flag - when true, calls both Cloud Function AND Direct Read for comparison
let debugComparisonMode = false;

export const setDebugComparisonMode = (enabled) => {
  debugComparisonMode = enabled;
  console.log(`🔧 Debug Comparison Mode: ${enabled ? "ENABLED" : "DISABLED"}`);
  console.log(
    enabled
      ? "⚡ Will call both Cloud Function AND Direct Read for performance comparison"
      : "📊 Normal operation mode",
  );
};

export const getDebugComparisonMode = () => debugComparisonMode;

// Storage system detection cache
const storageSystemCache = new Map();
const STORAGE_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

const getStorageFromCache = (uid, companyId) => {
  const key = `${uid}|${companyId}`;
  const entry = storageSystemCache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.time > STORAGE_CACHE_TTL_MS) {
    storageSystemCache.delete(key);
    return null;
  }
  return entry.system;
};

const putStorageInCache = (uid, companyId, system) => {
  const key = `${uid}|${companyId}`;
  storageSystemCache.set(key, { system, time: Date.now() });
};

// Presence cache (fed by TransactionContext via useAllYearsTransactions)
const unifiedPresenceCache = new Map();
const setUnifiedPresence = (uid, companyId, hasData) => {
  const key = `${uid}|${companyId}`;
  unifiedPresenceCache.set(key, { hasData: !!hasData, time: Date.now() });
};
const getUnifiedPresence = (uid, companyId) => {
  const key = `${uid}|${companyId}`;
  const entry = unifiedPresenceCache.get(key);
  return entry ? !!entry.hasData : null;
};

// De-dupe in-flight detection calls
const inflightDetection = new Map();

// Clear storage cache for a specific company (useful after marking as unified)
export const clearStorageCache = (uid, companyId) => {
  const key = `${uid}|${companyId}`;
  storageSystemCache.delete(key);
  unifiedPresenceCache.delete(key); // Also clear presence cache
  console.log(`🗑️ Cleared storage cache for ${companyId}`);
};

// Clear ALL storage caches (useful for debugging or forcing full re-detect)
export const clearAllStorageCaches = () => {
  storageSystemCache.clear();
  unifiedPresenceCache.clear();
  console.log(`🗑️ Cleared ALL storage caches`);
};

// Company storage system detection
export const detectStorageSystem = async (uid, companyId) => {
  // Ensure companyId is a string (Firebase equalTo() requires primitive values)
  if (typeof companyId === 'object' && companyId !== null) {
    console.warn("⚠️ companyId is an object, attempting to extract string value:", companyId);
    // Prioritize sameCompanyKey since that's what we query for in the database
    companyId = companyId.sameCompanyKey || companyId.companyId || companyId.id || String(companyId);
  }
  
  // Convert to string to ensure primitive value
  companyId = companyId ? String(companyId) : null;
  
  console.log("🔍 Detecting storage system for:", { uid, companyId });

  if (!uid || !companyId) {
    console.log("❌ Missing uid or companyId, defaulting to unified");
    return "unified";
  }

  // Check cache first
  const cached = getStorageFromCache(uid, companyId);
  if (cached) {
    console.log(`💾 Cache hit for ${companyId}: ${cached}`);
    return cached;
  }

  // Short-circuit with presence from TransactionContext (already using unified in-app)
  const presence = getUnifiedPresence(uid, companyId);
  if (presence === true) {
    console.log(`⚡ Using context presence for ${companyId}: unified`);
    putStorageInCache(uid, companyId, "unified");
    return "unified";
  }

  // Share in-flight detection promises to avoid duplicate reads
  const inflightKey = `${uid}|${companyId}`;
  if (inflightDetection.has(inflightKey)) {
    return inflightDetection.get(inflightKey);
  }

  const detectPromise = (async () => {
    try {
      // � OPTIMIZATION: Check lightweight setting path first (Direct O(1) Lookup)
      // This avoids downloading the heavy ClientCompanyAdded node (6MB+) just to check metadata
      const settingsRef = ref(
        database,
        `/BusinessDetails/${uid}/CompanySettings/${companyId}/storageSystem`,
      );
      const settingsSnap = await get(settingsRef);

      if (settingsSnap.exists()) {
        const fastSystem = settingsSnap.val();
        estimateDataSize(fastSystem, `FastDetect check (${companyId})`);
        console.log(
          `✅ [FastDetect] Found storage system in lightweight path: ${fastSystem}`,
        );
        putStorageInCache(uid, companyId, fastSystem);
        return fastSystem;
      }

      // �🔥 OPTIMIZED: Query only matching companies instead of loading entire database
      const companiesRef = ref(database, `/User/${uid}/ClientCompanyAdded`);
      const companiesQuery = query(
        companiesRef,
        orderByChild("sameCompanyKey"),
        equalTo(companyId),
      );
      const snapshot = await get(companiesQuery);

      // 📊 LOG DOWNLOAD SIZE
      if (snapshot.exists()) {
        estimateDataSize(snapshot.val(), `detectStorageSystem(${companyId})`);
      }

      let explicitSystem = null; // 'legacy' | 'unified' | null
      let hasLegacyData = false;
      let hasUnifiedData = false;

      if (snapshot.exists()) {
        const matchingCompanies = snapshot.val() || {};

        // 1) Explicit flag precedence
        for (const companyKey in matchingCompanies) {
          const node = matchingCompanies[companyKey] || {};
          const stored =
            node.storageSystem || node.StorageSystem || node.storage_system;
          const normalized = (stored || "").toString().toLowerCase();
          if (normalized === "firestoreunified") {
            explicitSystem = "firestoreUnified";
            break; // Firestore overrides all
          }
          if (normalized === "legacy") {
            explicitSystem = explicitSystem || "legacy"; // keep if firestore not found
          }
          if (normalized === "unified") {
            explicitSystem = explicitSystem || "unified"; // keep if firestore/legacy not found
          }
        }

        // 2) Any legacy data across ANY year nodes?
        if (!hasLegacyData) {
          for (const companyKey in matchingCompanies) {
            const companyData = matchingCompanies[companyKey] || {};
            const yearKeys = Object.keys(companyData).filter(
              (k) =>
                k !== "sameCompanyKey" &&
                k !== "storageSystem" &&
                k !== "companyNameRef",
            );
            for (const y of yearKeys) {
              const accounts = companyData?.[y]?.Accounts;
              if (accounts && Object.keys(accounts || {}).length > 0) {
                hasLegacyData = true;
                break;
              }
            }
            if (hasLegacyData) break;
          }
        }
      }

      // NOTE: Removed fallback direct path check - only affects a handful of legacy clients
      // that can be fixed manually by ensuring their sameCompanyKey is set correctly
      // or by removing the explicit storageSystem: "unified" flag from the database

      // 3) Unified data presence using companyId as sameCompanyKey (modular key)
      try {
        const unifiedRef = ref(
          database,
          `/User/${uid}/UnifiedTransactions/${companyId}`,
        );
        // Only fetch one child to test for presence
        const unifiedSnap = await get(
          query(unifiedRef, orderByKey(), limitToFirst(1)),
        );
        hasUnifiedData = unifiedSnap.exists();
      } catch (e) {
        // Ignore unified presence errors
      }

      // Precedence (UPDATED after migration support):
      // explicit firestoreUnified > explicit unified > explicit legacy > actual legacy data > unified data > default unified

      // 🚀 SELF-HEALING: Define helper to populate fast path
      const backfillFastPath = (sys) => {
        try {
          const path = `/BusinessDetails/${uid}/CompanySettings/${companyId}/storageSystem`;
          console.log(
            `🛠️ [Self-Healing] Backfilling storage system to: ${path} = ${sys}`,
          );
          database
            .ref()
            .update({ [path]: sys })
            .catch(() => {});
        } catch (e) {
          /* ignore */
        }
      };

      if (explicitSystem === "firestoreUnified") {
        console.log(
          `✅ Detected Firestore unified system for ${companyId} (explicit flag)`,
        );
        putStorageInCache(uid, companyId, "firestoreUnified");
        backfillFastPath("firestoreUnified");
        return "firestoreUnified";
      }
      if (explicitSystem === "unified") {
        console.log(
          `✅ Detected unified system for ${companyId} (explicit flag - post-migration)`,
        );
        if (hasLegacyData) {
          console.log(
            `ℹ️ Company ${companyId} has legacy data but explicit unified flag - using unified (data was migrated)`,
          );
        }
        putStorageInCache(uid, companyId, "unified");
        backfillFastPath("unified");
        return "unified";
      }
      if (explicitSystem === "legacy") {
        console.log(
          `✅ Detected legacy system for ${companyId} (explicit flag)`,
        );
        putStorageInCache(uid, companyId, "legacy");
        backfillFastPath("legacy");
        return "legacy";
      }
      if (hasLegacyData) {
        console.log(
          `✅ Detected legacy system for ${companyId} (has year-based data, no explicit flag)`,
        );
        putStorageInCache(uid, companyId, "legacy");
        backfillFastPath("legacy"); // Explicitly set legacy to avoid re-scanning
        return "legacy";
      }
      if (hasUnifiedData) {
        console.log(
          `✅ Detected unified system for ${companyId} (has unified data)`,
        );
        putStorageInCache(uid, companyId, "unified");
        backfillFastPath("unified");
        return "unified";
      }
      console.log(
        `✅ Defaulting to unified system for ${companyId} (no data found)`,
      );
      putStorageInCache(uid, companyId, "unified");
      backfillFastPath("unified");
      return "unified";
    } catch (error) {
      console.error("Error detecting storage system:", error);
      return "unified"; // Default to unified on error (don't cache errors)
    } finally {
      inflightDetection.delete(inflightKey);
    }
  })();

  inflightDetection.set(inflightKey, detectPromise);
  return detectPromise;
};

// Mark company as using unified storage system
export const markCompanyAsUnified = async (uid, companyId) => {
  // Ensure companyId is a string (Firebase equalTo() requires primitive values)
  if (typeof companyId === 'object' && companyId !== null) {
    console.warn("⚠️ companyId is an object, attempting to extract string value:", companyId);
    companyId = companyId.sameCompanyKey || companyId.companyId || companyId.id || String(companyId);
  }
  companyId = companyId ? String(companyId) : null;
  
  try {
    // 🔥 OPTIMIZED: Query only matching companies instead of loading entire database
    const companiesRef = ref(database, `/User/${uid}/ClientCompanyAdded`);
    const companiesQuery = query(
      companiesRef,
      orderByChild("sameCompanyKey"),
      equalTo(companyId),
    );
    const snapshot = await get(companiesQuery);

    if (snapshot.exists() && snapshot.val()) {
      const matchingCompanies = snapshot.val();
      const updates = {};

      // Find all entries with matching sameCompanyKey and mark them as unified
      for (const companyKey in matchingCompanies) {
        if (
          matchingCompanies[companyKey] &&
          matchingCompanies[companyKey].sameCompanyKey === companyId
        ) {
          updates[
            `/User/${uid}/ClientCompanyAdded/${companyKey}/storageSystem`
          ] = "unified";
        }
      }

      // 🚀 ALSO update the lightweight path for future O(1) lookups
      updates[
        `/BusinessDetails/${uid}/CompanySettings/${companyId}/storageSystem`
      ] = "unified";

      if (Object.keys(updates).length > 0) {
        await database.ref().update(updates);
        console.log(
          `✅ Marked ${
            Object.keys(updates).length
          } company entries as unified for ${companyId}`,
        );
        // Clear cache after marking as unified
        clearStorageCache(uid, companyId);
      }
    }
  } catch (error) {
    console.error("Error marking company as unified:", error);
  }
};

// -------------------------
// Migration: Convert Legacy to Unified Storage
// -------------------------
/**
 * Migrate a company from legacy year-based storage to unified storage.
 * This function:
 * 1. Reads all transactions from legacy year-based paths
 * 2. Writes them to unified storage path
 * 3. Marks the company as unified
 * 4. Optionally deletes legacy data
 *
 * @param {string} uid - User ID
 * @param {string} companyId - Company's sameCompanyKey
 * @param {Object} options - Migration options
 * @param {boolean} options.deleteLegacy - Whether to delete legacy data after migration (default: false)
 * @param {function} options.onProgress - Progress callback (percentage, message)
 * @returns {Promise<{success: boolean, migratedCount: number, error?: string}>}
 */
export const migrateToUnified = async (uid, companyId, options = {}) => {
  // Ensure companyId is a string (Firebase equalTo() requires primitive values)
  if (typeof companyId === 'object' && companyId !== null) {
    console.warn("⚠️ companyId is an object, attempting to extract string value:", companyId);
    companyId = companyId.sameCompanyKey || companyId.companyId || companyId.id || String(companyId);
  }
  companyId = companyId ? String(companyId) : null;
  
  const { deleteLegacy = false, onProgress = () => {} } = options;

  console.log(`🚀 Starting migration to unified for company: ${companyId}`);
  onProgress(0, "Starting migration...");

  try {
    // 1. Find all company entries with matching sameCompanyKey
    const companiesRef = ref(database, `/User/${uid}/ClientCompanyAdded`);
    const companiesQuery = query(
      companiesRef,
      orderByChild("sameCompanyKey"),
      equalTo(companyId),
    );
    const snapshot = await get(companiesQuery);

    if (!snapshot.exists()) {
      return { success: false, migratedCount: 0, error: "Company not found" };
    }

    const matchingCompanies = snapshot.val() || {};
    const allTransactions = [];
    const legacyPaths = [];

    onProgress(10, "Reading legacy transactions...");

    // 2. Collect all legacy transactions from all year nodes
    for (const companyKey in matchingCompanies) {
      const companyData = matchingCompanies[companyKey] || {};
      const yearKeys = Object.keys(companyData).filter(
        (k) =>
          k !== "sameCompanyKey" &&
          k !== "storageSystem" &&
          k !== "companyNameRef" &&
          k !== "AmendedYearEnd" &&
          !k.startsWith("_"),
      );

      for (const year of yearKeys) {
        const accounts = companyData?.[year]?.Accounts;
        if (accounts && typeof accounts === "object") {
          for (const txKey in accounts) {
            const tx = accounts[txKey];
            if (tx && typeof tx === "object") {
              allTransactions.push({
                key: txKey,
                data: { ...tx, _migratedFrom: `${companyKey}/${year}` },
              });
            }
          }
          legacyPaths.push(
            `/User/${uid}/ClientCompanyAdded/${companyKey}/${year}/Accounts`,
          );
        }
      }
    }

    if (allTransactions.length === 0) {
      // No legacy data, just mark as unified
      await markCompanyAsUnified(uid, companyId);
      return {
        success: true,
        migratedCount: 0,
        message: "No legacy data found, marked as unified",
      };
    }

    onProgress(
      30,
      `Found ${allTransactions.length} transactions to migrate...`,
    );

    // 3. Write transactions to unified storage in batches
    const BATCH_SIZE = 500;
    const batches = [];
    for (let i = 0; i < allTransactions.length; i += BATCH_SIZE) {
      batches.push(allTransactions.slice(i, i + BATCH_SIZE));
    }

    let migratedCount = 0;
    for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
      const batch = batches[batchIndex];
      const updates = {};

      for (const { key, data } of batch) {
        updates[`/User/${uid}/UnifiedTransactions/${companyId}/${key}`] = data;
      }

      await database.ref().update(updates);
      migratedCount += batch.length;

      const progress =
        30 + Math.round(((batchIndex + 1) / batches.length) * 40);
      onProgress(
        progress,
        `Migrated ${migratedCount} of ${allTransactions.length} transactions...`,
      );
    }

    onProgress(75, "Marking company as unified...");

    // 4. Mark company as unified
    await markCompanyAsUnified(uid, companyId);

    // 5. Optionally delete legacy data
    if (deleteLegacy && legacyPaths.length > 0) {
      onProgress(85, "Cleaning up legacy data...");
      const deleteUpdates = {};
      for (const path of legacyPaths) {
        deleteUpdates[path] = null;
      }
      await database.ref().update(deleteUpdates);
      console.log(`🗑️ Deleted ${legacyPaths.length} legacy paths`);
    }

    onProgress(100, "Migration complete!");
    console.log(
      `✅ Successfully migrated ${migratedCount} transactions to unified storage`,
    );

    // Clear caches
    clearStorageCache(uid, companyId);

    return { success: true, migratedCount };
  } catch (error) {
    console.error("❌ Migration failed:", error);
    return { success: false, migratedCount: 0, error: error.message };
  }
};

/**
 * Fix partial migration by intelligently merging legacy and existing unified transactions.
 * This function:
 * 1. Reads existing unified transactions
 * 2. Reads all legacy transactions
 * 3. Identifies missing transactions (in legacy but not in unified)
 * 4. Adds only missing transactions to unified storage
 * 5. Preserves all existing unified transactions
 *
 * @param {string} uid - User ID
 * @param {string} companyId - Company's sameCompanyKey
 * @param {Object} options - Migration options
 * @param {function} options.onProgress - Progress callback (percentage, message)
 * @returns {Promise<{success: boolean, existingCount: number, addedCount: number, totalCount: number, error?: string}>}
 */
export const fixPartialMigration = async (uid, companyId, options = {}) => {
  // Ensure companyId is a string (Firebase equalTo() requires primitive values)
  if (typeof companyId === 'object' && companyId !== null) {
    console.warn("⚠️ companyId is an object, attempting to extract string value:", companyId);
    companyId = companyId.sameCompanyKey || companyId.companyId || companyId.id || String(companyId);
  }
  companyId = companyId ? String(companyId) : null;
  
  const { onProgress = () => {} } = options;

  console.log(`🔧 Starting partial migration fix for company: ${companyId}`);
  onProgress(0, "Starting migration fix...");

  try {
    // 1. Read existing unified transactions
    onProgress(5, "Reading existing unified transactions...");
    const unifiedRef = ref(
      database,
      `/User/${uid}/UnifiedTransactions/${companyId}`,
    );
    const unifiedSnapshot = await get(unifiedRef);

    const existingUnified = {};
    const existingKeys = new Set();

    if (unifiedSnapshot.exists()) {
      const unifiedData = unifiedSnapshot.val() || {};
      Object.entries(unifiedData).forEach(([key, tx]) => {
        existingUnified[key] = tx;
        existingKeys.add(key);
      });
    }

    const existingCount = existingKeys.size;
    console.log(`📊 Found ${existingCount} existing unified transactions`);
    onProgress(15, `Found ${existingCount} existing unified transactions...`);

    // 2. Read all legacy transactions
    onProgress(20, "Reading legacy transactions...");
    const companiesRef = ref(database, `/User/${uid}/ClientCompanyAdded`);
    const companiesQuery = query(
      companiesRef,
      orderByChild("sameCompanyKey"),
      equalTo(companyId),
    );
    const snapshot = await get(companiesQuery);

    if (!snapshot.exists()) {
      return {
        success: false,
        existingCount,
        addedCount: 0,
        totalCount: existingCount,
        error: "Company not found",
      };
    }

    const matchingCompanies = snapshot.val() || {};
    const legacyTransactions = [];

    // 3. Collect all legacy transactions
    for (const companyKey in matchingCompanies) {
      const companyData = matchingCompanies[companyKey] || {};
      const yearKeys = Object.keys(companyData).filter(
        (k) =>
          k !== "sameCompanyKey" &&
          k !== "storageSystem" &&
          k !== "companyNameRef" &&
          k !== "AmendedYearEnd" &&
          !k.startsWith("_"),
      );

      for (const year of yearKeys) {
        const accounts = companyData?.[year]?.Accounts;
        if (accounts && typeof accounts === "object") {
          for (const txKey in accounts) {
            const tx = accounts[txKey];
            if (tx && typeof tx === "object") {
              legacyTransactions.push({
                key: txKey,
                data: { ...tx, _migratedFrom: `${companyKey}/${year}` },
              });
            }
          }
        }
      }
    }

    const legacyCount = legacyTransactions.length;
    console.log(`📊 Found ${legacyCount} legacy transactions`);
    onProgress(35, `Found ${legacyCount} legacy transactions...`);

    // 4. Identify missing transactions (in legacy but not in unified)
    const missingTransactions = legacyTransactions.filter(
      ({ key }) => !existingKeys.has(key),
    );

    const missingCount = missingTransactions.length;
    console.log(`📊 Identified ${missingCount} missing transactions to add`);

    if (missingCount === 0) {
      onProgress(100, "All transactions already in unified storage!");
      return {
        success: true,
        existingCount,
        addedCount: 0,
        totalCount: existingCount,
        message: "All legacy transactions already exist in unified storage",
      };
    }

    onProgress(
      45,
      `Adding ${missingCount} missing transactions to unified storage...`,
    );

    // 5. Add missing transactions to unified storage in batches
    const BATCH_SIZE = 500;
    const batches = [];
    for (let i = 0; i < missingTransactions.length; i += BATCH_SIZE) {
      batches.push(missingTransactions.slice(i, i + BATCH_SIZE));
    }

    let addedCount = 0;
    for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
      const batch = batches[batchIndex];
      const updates = {};

      for (const { key, data } of batch) {
        updates[`/User/${uid}/UnifiedTransactions/${companyId}/${key}`] = data;
      }

      await database.ref().update(updates);
      addedCount += batch.length;

      const progress =
        45 + Math.round(((batchIndex + 1) / batches.length) * 40);
      onProgress(
        progress,
        `Added ${addedCount} of ${missingCount} transactions...`,
      );
    }

    onProgress(90, "Marking company as unified...");

    // 6. Ensure company is marked as unified
    await markCompanyAsUnified(uid, companyId);

    onProgress(100, "Migration fix complete!");
    const totalCount = existingCount + addedCount;
    console.log(
      `✅ Migration fix complete: ${existingCount} existing + ${addedCount} added = ${totalCount} total transactions`,
    );

    // Clear caches
    clearStorageCache(uid, companyId);

    return {
      success: true,
      existingCount,
      addedCount,
      totalCount,
      message: `Successfully added ${addedCount} missing transactions. Total: ${totalCount}`,
    };
  } catch (error) {
    console.error("❌ Migration fix failed:", error);
    return {
      success: false,
      existingCount: 0,
      addedCount: 0,
      totalCount: 0,
      error: error.message,
    };
  }
};

// -------------------------
// Audit logging (client-side)
// -------------------------
/**
 * Check whether the user has opted-in to audit logging.
 * Reads `/User/{uid}/Preferences/auditLogsEnabled` from Realtime DB.
 */
export const checkAuditOptIn = async (uid) => {
  try {
    if (!uid) return false;
    const prefRef = ref(database, `/Owner/${uid}/Preferences/auditLogsEnabled`);
    const snap = await get(prefRef);
    return !!snap.val();
  } catch (err) {
    console.error("❌ checkAuditOptIn error:", err);
    return false;
  }
};

/**
 * Send an audit event via Firebase Callable Function `auditLog`.
 * Respects user opt-in and will no-op when disabled.
 */
export const logAuditToCloud = async (uid, companyId, action, details = {}) => {
  try {
    const enabled = await checkAuditOptIn(uid);
    if (!enabled) return;

    if (!functions || typeof functions.httpsCallable !== "function") {
      console.warn("⚠️ Firebase functions not available - audit not sent");
      return;
    }

    const payload = {
      action,
      companyId,
      details,
      timestamp: new Date().toISOString(),
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    };

    const fn = functions.httpsCallable("auditLog");
    // Await so errors are visible but don't block caller if desired.
    await fn(payload);
    console.log("📝 Audit logged via callable function:", action, {
      companyId,
      uid,
    });
  } catch (err) {
    console.error("❌ logAuditToCloud error:", err);
  }
};

// ------------------------------------------------------------
// Performance Monitoring
// ------------------------------------------------------------
const performanceMetrics = {
  cloudFunction: { calls: 0, successes: 0, failures: 0, totalTime: 0 },
  directRead: { calls: 0, successes: 0, failures: 0, totalTime: 0 },
};

const logPerformance = (method, success, duration) => {
  const metrics = performanceMetrics[method];
  if (!metrics) return;

  metrics.calls++;
  if (success) {
    metrics.successes++;
  } else {
    metrics.failures++;
  }
  metrics.totalTime += duration;

  const avgTime = metrics.totalTime / metrics.calls;
  const successRate = ((metrics.successes / metrics.calls) * 100).toFixed(1);

  console.log(
    `📊 Performance [${method}]: ${duration}ms | Avg: ${avgTime.toFixed(
      0,
    )}ms | Success Rate: ${successRate}% (${metrics.successes}/${
      metrics.calls
    })`,
  );
};

export const getPerformanceStats = () => {
  return {
    cloudFunction: {
      ...performanceMetrics.cloudFunction,
      avgTime:
        performanceMetrics.cloudFunction.calls > 0
          ? (
              performanceMetrics.cloudFunction.totalTime /
              performanceMetrics.cloudFunction.calls
            ).toFixed(0)
          : 0,
      successRate:
        performanceMetrics.cloudFunction.calls > 0
          ? (
              (performanceMetrics.cloudFunction.successes /
                performanceMetrics.cloudFunction.calls) *
              100
            ).toFixed(1)
          : 0,
    },
    directRead: {
      ...performanceMetrics.directRead,
      avgTime:
        performanceMetrics.directRead.calls > 0
          ? (
              performanceMetrics.directRead.totalTime /
              performanceMetrics.directRead.calls
            ).toFixed(0)
          : 0,
      successRate:
        performanceMetrics.directRead.calls > 0
          ? (
              (performanceMetrics.directRead.successes /
                performanceMetrics.directRead.calls) *
              100
            ).toFixed(1)
          : 0,
    },
  };
};

// Utility function to print performance stats to console
export const printPerformanceStats = () => {
  const stats = getPerformanceStats();
  console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
  console.log("📊 TRANSACTION LOADER PERFORMANCE STATS");
  console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
  console.log("\n🔵 Cloud Functions:");
  console.log(`   Calls: ${stats.cloudFunction.calls}`);
  console.log(`   Successes: ${stats.cloudFunction.successes}`);
  console.log(`   Failures: ${stats.cloudFunction.failures}`);
  console.log(`   Success Rate: ${stats.cloudFunction.successRate}%`);
  console.log(`   Avg Time: ${stats.cloudFunction.avgTime}ms`);
  console.log(`   Total Time: ${stats.cloudFunction.totalTime.toFixed(0)}ms`);

  console.log("\n🟢 Direct Database Reads:");
  console.log(`   Calls: ${stats.directRead.calls}`);
  console.log(`   Successes: ${stats.directRead.successes}`);
  console.log(`   Failures: ${stats.directRead.failures}`);
  console.log(`   Success Rate: ${stats.directRead.successRate}%`);
  console.log(`   Avg Time: ${stats.directRead.avgTime}ms`);
  console.log(`   Total Time: ${stats.directRead.totalTime.toFixed(0)}ms`);

  // Comparison
  if (stats.cloudFunction.calls > 0 && stats.directRead.calls > 0) {
    const cfAvg = parseFloat(stats.cloudFunction.avgTime);
    const drAvg = parseFloat(stats.directRead.avgTime);
    const faster = cfAvg < drAvg ? "Cloud Functions" : "Direct Reads";
    const speedup =
      cfAvg < drAvg
        ? ((drAvg / cfAvg - 1) * 100).toFixed(1)
        : ((cfAvg / drAvg - 1) * 100).toFixed(1);

    console.log(`\n⚡ Performance Winner: ${faster} (${speedup}% faster)`);
  }

  console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n");

  return stats;
};

// Make it available globally for debugging
if (typeof window !== "undefined") {
  window.getTransactionLoaderStats = printPerformanceStats;
  window.enableDebugComparison = () => setDebugComparisonMode(true);
  window.disableDebugComparison = () => setDebugComparisonMode(false);
  window.toggleDebugComparison = () =>
    setDebugComparisonMode(!debugComparisonMode);

  console.log("🔧 Debug commands available:");
  console.log(
    "   window.getTransactionLoaderStats() - Print performance stats",
  );
  console.log(
    "   window.enableDebugComparison()     - Enable side-by-side comparison",
  );
  console.log(
    "   window.disableDebugComparison()    - Disable side-by-side comparison",
  );
  console.log("   window.toggleDebugComparison()     - Toggle debug mode");
}

// ------------------------------------------------------------
// Simple in-memory cache to avoid refetching identical queries
// Keyed by uid|companyId|year|system|start|end|scope
// ------------------------------------------------------------
const transactionsCache = new Map();
const CACHE_TTL_MS = 60 * 1000; // 60s TTL

const makeCacheKey = (
  scope, // 'single' | 'allyears'
  {
    uid,
    companyId,
    yearSelected = "",
    storageSystem = "",
    startDate = "",
    endDate = "",
  },
) =>
  `${scope}|${uid}|${companyId}|${yearSelected}|${storageSystem}|${startDate}|${endDate}`;

const getFromCache = (key) => {
  const entry = transactionsCache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.time > CACHE_TTL_MS) {
    transactionsCache.delete(key);
    return null;
  }
  return entry.data;
};

const putInCache = (key, data) => {
  transactionsCache.set(key, { data, time: Date.now() });
};

/**
 * Load transactions using the legacy year-based system
 */
export const loadLegacyTransactions = async (
  uid,
  companyId,
  yearSelected,
  dateFilters = {},
) => {
  // Ensure companyId is a string (Firebase equalTo() requires primitive values)
  if (typeof companyId === 'object' && companyId !== null) {
    console.warn("⚠️ companyId is an object, attempting to extract string value:", companyId);
    companyId = companyId.sameCompanyKey || companyId.companyId || companyId.id || String(companyId);
  }
  companyId = companyId ? String(companyId) : null;
  
  const { startDate, endDate } = dateFilters;

  // Narrow the company scan using sameCompanyKey
  const companiesRef = ref(database, `/User/${uid}/ClientCompanyAdded`);
  const companyQuery = query(
    companiesRef,
    orderByChild("sameCompanyKey"),
    equalTo(companyId),
  );
  const companiesSnap = await get(companyQuery);
  const companies = companiesSnap.val() || {};

  let targetCompanyKey = null;
  for (const companyKey in companies) {
    const companyData = companies[companyKey];
    if (
      companyData &&
      companyData[yearSelected] &&
      companyData[yearSelected].Accounts
    ) {
      targetCompanyKey = companyKey;
      break;
    }
  }

  if (!targetCompanyKey) return [];

  const accountsRef = ref(
    database,
    `/User/${uid}/ClientCompanyAdded/${targetCompanyKey}/${yearSelected}/Accounts`,
  );

  // Apply server-side date window if provided
  let accountsQuery = accountsRef;
  const hasStart = !!startDate;
  const hasEnd = !!endDate;
  if (hasStart || hasEnd) {
    const parts = [orderByChild("date")];
    if (hasStart) parts.push(startAt(startDate));
    if (hasEnd) parts.push(endAt(endDate));
    accountsQuery = query(accountsRef, ...parts);
  }

  const accSnap = await get(accountsQuery);
  const data = accSnap.val() || {};

  let transactions = Object.keys(data).map((key) => ({
    id: key,
    ...data[key],
  }));

  // Client-side safety filter in case of non-ISO date strings
  if (hasStart || hasEnd) {
    transactions = transactions.filter((t) => {
      if (!t.date) return true;
      const d = new Date(t.date);
      if (hasStart && d < new Date(startDate)) return false;
      if (hasEnd && d > new Date(endDate)) return false;
      return true;
    });
  }

  // Remove retained income placeholders only (keep journals)
  transactions = transactions.filter((t) => t.date !== "RetainedIncomeForTB");

  return transactions;
};

/**
 * Load transactions using the new unified storage system
 */
export const loadUnifiedTransactions = async (
  uid,
  companyId,
  yearSelected,
  dateFilters = {},
) => {
  const startTime = performance.now();
  try {
    const { startDate, endDate } = dateFilters;

    console.log("⏱️ [CloudFunction] Starting LoadUnifiedTransactions...");

    // Enable emulator for testing
    // functions.useEmulator("127.0.0.1", 5001);
    // Call cloud function to get unified transactions
    const loadUnifiedData = functions.httpsCallable("LoadUnifiedTransactions");

    // Add timeout to prevent hanging
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(
        () => reject(new Error("LoadUnifiedTransactions timeout")),
        30000,
      );
    });

    const functionPromise = loadUnifiedData({
      clientId: companyId,
      yearSelected,
      startDate,
      endDate,
      ownerUid: uid,
    });

    const result = await Promise.race([functionPromise, timeoutPromise]);
    const duration = performance.now() - startTime;

    console.log("📡 LoadUnifiedTransactions result received:", {
      hasData: !!result.data,
      transactionsLength: result.data?.transactions?.length || 0,
      error: result.data?.error,
      duration: `${duration.toFixed(0)}ms`,
    });

    if (result.data && result.data.transactions) {
      logPerformance("cloudFunction", true, duration);
      // Add legacy-compatible properties for backward compatibility
      return result.data.transactions.map((transaction) => ({
        ...transaction,
        // Ensure legacy properties exist with defaults
        year: transaction.year || yearSelected || "Unknown",
        companyKey: transaction.companyKey || "unified",
        yearKey: transaction.yearKey || yearSelected || "unified",
        // Ensure rerouting properties have defaults
        JE: transaction.JE || false,
        key: transaction.key || transaction.id,
        bankAccount: transaction.bankAccount || "",
        assessed: transaction.assessed || false,
        accountNames: getTransactionDescription(transaction),
        openingBalance: transaction.openingBalance || false,
      }));
    }

    logPerformance("cloudFunction", false, duration);
    return [];
  } catch (error) {
    const duration = performance.now() - startTime;
    console.error(
      "❌ Error loading unified transactions:",
      error,
      `(${duration.toFixed(0)}ms)`,
    );
    logPerformance("cloudFunction", false, duration);
    // Fallback to empty array on error instead of throwing
    return [];
  }
};

/**
 * Universal transaction loader that automatically detects and uses the appropriate system
 */
export const loadTransactions = async (
  uid,
  companyId,
  yearSelected,
  dateFilters = {},
  knownStorageSystem = null,
) => {
  try {
    const storageSystem =
      knownStorageSystem || (await detectStorageSystem(uid, companyId));

    // Cache lookup for per-year scope
    const cacheKey = makeCacheKey("single", {
      uid,
      companyId,
      yearSelected,
      storageSystem,
      startDate: dateFilters.startDate,
      endDate: dateFilters.endDate,
    });
    const cached = getFromCache(cacheKey);
    if (cached) return cached;

    if (storageSystem === "firestoreUnified") {
      console.log(`🔥 Using Firestore for ${companyId}`);
      const data = await firestoreAdapter.getTransactions(companyId, {
        startDate: dateFilters.startDate,
        endDate: dateFilters.endDate,
        orderByField: "date",
        orderDirection: "asc",
      });
      putInCache(cacheKey, data);
      return data;
    } else if (storageSystem === "unified") {
      const data = await loadUnifiedTransactions(
        uid,
        companyId,
        yearSelected,
        dateFilters,
      );
      putInCache(cacheKey, data);
      return data;
    } else {
      const data = await loadLegacyTransactions(
        uid,
        companyId,
        yearSelected,
        dateFilters,
      );
      putInCache(cacheKey, data);
      return data;
    }
  } catch (error) {
    console.error("Error in universal transaction loader:", error);
    // Fallback to legacy system
    const data = await loadLegacyTransactions(
      uid,
      companyId,
      yearSelected,
      dateFilters,
    );
    return data;
  }
};

/**
 * Load all years' transactions for a company (used by TBalance and other components)
 *
 * IMPORTANT FILTERING BEHAVIOR:
 * - For Statement of Financial Position (SOFP/Balance Sheet): Pass ONLY endDate, NO startDate
 *   SOFP is cumulative and needs ALL transactions from the beginning up to the end date
 *   to correctly calculate retained earnings/accumulated loss
 *
 * - For Statement of Comprehensive Income (SOCI/P&L): Pass BOTH startDate and endDate
 *   SOCI shows performance for a specific period
 *
 * - For Trial Balance: Depends on usage - typically pass ONLY endDate for cumulative balance
 *
 * @param {string} uid - User ID
 * @param {string} companyId - Company ID (sameCompanyKey)
 * @param {object} dateFilters - Optional filters: { startDate, endDate, assessed }
 *                                Only provide startDate when period-specific data is needed
 *                                assessed: true = only assessed, false = unassessed/missing, undefined = all
 * @returns {Promise<Array>} - Array of transactions
 */
export const loadAllYearsTransactions = async (
  uid,
  companyId,
  dateFilters = {},
) => {
  // Ensure companyId is a string (Firebase equalTo() requires primitive values)
  if (typeof companyId === 'object' && companyId !== null) {
    console.warn("⚠️ companyId is an object, attempting to extract string value:", companyId);
    companyId = companyId.sameCompanyKey || companyId.companyId || companyId.id || String(companyId);
  }
  companyId = companyId ? String(companyId) : null;
  
  console.log("🔄 loadAllYearsTransactions called with:", {
    uid,
    companyId,
    dateFilters,
  });

  try {
    const storageSystem = await detectStorageSystem(uid, companyId);
    console.log(
      "📁 Detected storage system:",
      storageSystem,
      "for company:",
      companyId,
    );

    if (storageSystem === "firestoreUnified") {
      console.log("🔥 Using Firestore for company:", companyId);
      const startTime = performance.now();

      try {
        console.log("📊 [Firestore Query] Parameters:", {
          startDate: dateFilters.startDate,
          endDate: dateFilters.endDate,
          assessed: dateFilters.assessed,
          assessedMeaning:
            dateFilters.assessed === true
              ? "HISTORICAL (assessed=true)"
              : dateFilters.assessed === false
                ? "NEW (assessed missing/false)"
                : "ALL (no filter)",
        });

        const transactions = await firestoreAdapter.getTransactions(companyId, {
          startDate: dateFilters.startDate,
          endDate: dateFilters.endDate,
          assessed: dateFilters.assessed, // CRITICAL: Pass assessed filter to Firestore
          orderByField: "date",
          orderDirection: "asc",
        });

        const duration = performance.now() - startTime;
        console.log(
          `✅ Firestore: Loaded ${
            transactions.length
          } transactions in ${duration.toFixed(0)}ms`,
        );

        return transactions;
      } catch (error) {
        const duration = performance.now() - startTime;
        console.error(
          `❌ Firestore error after ${duration.toFixed(0)}ms:`,
          error,
        );
        return [];
      }
    }

    if (storageSystem === "unified") {
      console.log("🌐 Using unified storage system for company:", companyId);

      let cloudFunctionPromise = null;
      let result = null;

      // DEBUG MODE: Start cloud function simultaneously for comparison
      if (debugComparisonMode) {
        console.log(
          "🔧 [DEBUG] Starting parallel Cloud Function call for comparison...",
        );
        const cfStartTimeDebug = performance.now();

        const loadAllUnifiedData = functions.httpsCallable(
          "LoadAllUnifiedTransactions",
        );

        cloudFunctionPromise = (async () => {
          try {
            const timeoutMs = 30000;
            const timeoutPromise = new Promise((_, reject) =>
              setTimeout(
                () => reject(new Error("Cloud Function timeout")),
                timeoutMs,
              ),
            );

            const cfResult = await Promise.race([
              loadAllUnifiedData({
                clientId: companyId,
                startDate: dateFilters.startDate,
                endDate: dateFilters.endDate,
                ownerUid: uid,
              }),
              timeoutPromise,
            ]);

            const cfDuration = performance.now() - cfStartTimeDebug;
            const transactions = cfResult?.data?.transactions || [];

            console.log(
              `🔧 [DEBUG CloudFunction] Success: ${
                transactions.length
              } transactions in ${cfDuration.toFixed(0)}ms`,
            );
            logPerformance("cloudFunction", true, cfDuration);
            return { transactions, duration: cfDuration, rawResult: cfResult };
          } catch (err) {
            const cfDuration = performance.now() - cfStartTimeDebug;
            console.error(
              `🔧 [DEBUG CloudFunction] Error after ${cfDuration.toFixed(
                0,
              )}ms:`,
              err,
            );
            logPerformance("cloudFunction", false, cfDuration);
            return { transactions: [], duration: cfDuration, error: err };
          }
        })();
      }

      // PRIMARY METHOD: Direct Database Read
      const directStartTime = performance.now();
      console.log("⏱️ [DirectRead PRIMARY] Starting direct database read...");

      try {
        const unifiedRef = ref(
          database,
          `/User/${uid}/UnifiedTransactions/${companyId}`,
        );
        const unifiedSnap = await get(unifiedRef);

        if (unifiedSnap.exists()) {
          const unifiedData = unifiedSnap.val() || {};

          // 📊 LOG DOWNLOAD SIZE for the big transaction payload
          estimateDataSize(
            unifiedData,
            `Raw Transactions Download (${companyId})`,
          );

          let transactions = Object.keys(unifiedData).map((key) => ({
            id: key,
            ...unifiedData[key],
            year: unifiedData[key].year || "All Years",
            companyKey: "unified",
            yearKey: "unified",
            key: key,
          }));

          // Apply date filters
          const { startDate, endDate, assessed } = dateFilters;
          if (startDate || endDate) {
            const start = parseComparableDate(startDate);
            const end = parseComparableDate(endDate);
            transactions = transactions.filter((t) => {
              if (!t.date) return true;
              const d = parseComparableDate(t.date);
              if (!d) return true;
              if (start && d < start) return false;
              if (end && d > end) return false;
              return true;
            });
          }

          // NEW: Apply assessed filter client-side for RTDB
          // (RTDB doesn't support efficient OR queries for "assessed == false OR missing")
          // assessed === true: Historical tab - only assessed transactions
          // assessed === false: New tab - transactions missing assessed field OR assessed === false
          if (assessed === true) {
            console.log(
              "📊 [RTDB Filter] Filtering for HISTORICAL (assessed === true)",
            );
            // Historical: only assessed transactions
            const beforeCount = transactions.length;
            transactions = transactions.filter((t) => t.assessed === true);
            console.log(
              `📊 [RTDB Filter] Filtered ${beforeCount} → ${transactions.length} assessed transactions`,
            );
          } else if (assessed === false) {
            console.log(
              "📊 [RTDB Filter] Filtering for NEW (assessed missing/false)",
            );
            // New: transactions where assessed doesn't exist OR is explicitly false
            const beforeCount = transactions.length;
            transactions = transactions.filter(
              (t) =>
                t.assessed === undefined ||
                t.assessed === null ||
                t.assessed === false,
            );
            console.log(
              `📊 [RTDB Filter] Filtered ${beforeCount} → ${transactions.length} unassessed transactions`,
            );
          } else {
            console.log(
              "📊 [RTDB Filter] No assessed filter - showing ALL transactions",
            );
          }
          // If assessed is null/undefined, return all transactions (for reports/other pages)

          // Filter out retained income only (keep journals)
          transactions = transactions.filter(
            (t) => t.date !== "RetainedIncomeForTB",
          );

          const directDuration = performance.now() - directStartTime;
          console.log(
            `✅ [DirectRead PRIMARY] Success: ${
              transactions.length
            } transactions in ${directDuration.toFixed(0)}ms`,
          );
          logPerformance("directRead", true, directDuration);

          // DEBUG MODE: Wait for cloud function and compare
          if (debugComparisonMode && cloudFunctionPromise) {
            const cfResult = await cloudFunctionPromise;
            const drCount = transactions.length;
            const cfCount = cfResult.transactions.length;
            const timeDiff = (cfResult.duration - directDuration).toFixed(0);
            const faster =
              directDuration < cfResult.duration
                ? "DirectRead"
                : "CloudFunction";
            const speedPct = Math.abs(
              (timeDiff / Math.max(directDuration, cfResult.duration)) * 100,
            ).toFixed(1);

            console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
            console.log("🔧 DEBUG MODE - Side-by-Side Comparison");
            console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
            console.log(
              `� Direct Read:    ${directDuration.toFixed(
                0,
              )}ms, ${drCount} records`,
            );
            console.log(
              `📊 Cloud Function: ${cfResult.duration.toFixed(
                0,
              )}ms, ${cfCount} records`,
            );
            console.log(
              `⚡ Winner: ${faster} (${speedPct}% faster, ${Math.abs(
                timeDiff,
              )}ms difference)`,
            );
            console.log(
              `✅ Data Match: ${
                drCount === cfCount
                  ? "YES"
                  : "NO (DR:" + drCount + " vs CF:" + cfCount + ")"
              }`,
            );
            console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n");
          }

          // Add legacy-compatible properties for backward compatibility
          const data = transactions.map((transaction) => ({
            ...transaction,
            // Ensure legacy properties exist with defaults
            year: transaction.year || "All Years",
            companyKey: transaction.companyKey || "unified",
            yearKey: transaction.yearKey || "unified",
            // Ensure rerouting properties have defaults
            JE: transaction.JE || false,
            key: transaction.key || transaction.id,
            bankAccount: transaction.bankAccount || "",
            assessed: transaction.assessed || false,
            accountNames: getTransactionDescription(transaction),
            openingBalance: transaction.openingBalance || false,
          }));

          // Cache all-years unified
          const cacheKey = makeCacheKey("allyears", {
            uid,
            companyId,
            storageSystem,
            startDate: dateFilters.startDate,
            endDate: dateFilters.endDate,
          });
          putInCache(cacheKey, data);
          return data;
        } else {
          console.log(`⚠️ [DirectRead PRIMARY] No data found`);
          // Fall through to cloud function fallback
        }
      } catch (directError) {
        const directDuration = performance.now() - directStartTime;
        console.error(
          `❌ [DirectRead PRIMARY] Failed after ${directDuration.toFixed(
            0,
          )}ms:`,
          directError,
        );
        logPerformance("directRead", false, directDuration);
        // Fall through to cloud function fallback
      }

      // FALLBACK: Cloud Function (if direct read failed or returned no data)
      console.log(
        "🔄 Direct read failed/empty, trying Cloud Function fallback...",
      );

      const cfStartTime = performance.now();
      const loadAllUnifiedData = functions.httpsCallable(
        "LoadAllUnifiedTransactions",
      );

      console.log(
        "⏱️ [CloudFunction FALLBACK] Calling LoadAllUnifiedTransactions with:",
        {
          clientId: companyId,
          startDate: dateFilters.startDate,
          endDate: dateFilters.endDate,
          ownerUid: uid,
        },
      );

      try {
        const timeoutMs = 30000;
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(
            () => reject(new Error("LoadAllUnifiedTransactions timeout")),
            timeoutMs,
          ),
        );

        result = await Promise.race([
          loadAllUnifiedData({
            clientId: companyId,
            startDate: dateFilters.startDate,
            endDate: dateFilters.endDate,
            ownerUid: uid,
          }),
          timeoutPromise,
        ]);

        const cfDuration = performance.now() - cfStartTime;
        console.log(
          `📡 [CloudFunction FALLBACK] Completed in ${cfDuration.toFixed(0)}ms`,
        );

        if (
          result &&
          result.data &&
          Array.isArray(result.data.transactions) &&
          result.data.transactions.length > 0 &&
          !result.data.error
        ) {
          logPerformance("cloudFunction", true, cfDuration);

          // Add legacy-compatible properties for backward compatibility
          const data = result.data.transactions.map((transaction) => ({
            ...transaction,
            // Ensure legacy properties exist with defaults
            year: transaction.year || "All Years",
            companyKey: transaction.companyKey || "unified",
            yearKey: transaction.yearKey || "unified",
            // Ensure rerouting properties have defaults
            JE: transaction.JE || false,
            key: transaction.key || transaction.id,
            bankAccount: transaction.bankAccount || "",
            assessed: transaction.assessed || false,
            accountNames: getTransactionDescription(transaction),
            openingBalance: transaction.openingBalance || false,
          }));

          // Cache all-years unified
          const cacheKey = makeCacheKey("allyears", {
            uid,
            companyId,
            storageSystem,
            startDate: dateFilters.startDate,
            endDate: dateFilters.endDate,
          });
          putInCache(cacheKey, data);
          return data;
        }
      } catch (cfError) {
        const cfDuration = performance.now() - cfStartTime;
        console.error(
          `❌ [CloudFunction FALLBACK] Failed after ${cfDuration.toFixed(
            0,
          )}ms:`,
          cfError,
        );
        logPerformance("cloudFunction", false, cfDuration);
      }

      // Both methods failed - return empty array
      console.error(
        "❌ Both Direct Read and Cloud Function failed - returning empty array",
      );
      return [];
    } else {
      console.log("📚 Using legacy storage system for company:", companyId);
      // Legacy system - load from all years using sameCompanyKey
      try {
        const { startDate, endDate } = dateFilters;

        // Narrow companies by sameCompanyKey
        const companiesRef = ref(database, `/User/${uid}/ClientCompanyAdded`);
        const companiesQuery = query(
          companiesRef,
          orderByChild("sameCompanyKey"),
          equalTo(companyId),
        );
        const snapshot = await get(companiesQuery);
        const allCompaniesData = snapshot.val() || {};

        if (!allCompaniesData || Object.keys(allCompaniesData).length === 0) {
          return [];
        }

        let allTransactions = [];

        // The query above already limits to matching company entries
        const companyEntries = Object.keys(allCompaniesData);

        console.log(
          "📅 Found company entries for legacy system:",
          companyEntries,
          "for sameCompanyKey:",
          companyId,
        );

        for (const companyKey of companyEntries) {
          const companyData = allCompaniesData[companyKey];
          if (!companyData) continue;

          // Get all year keys within this company entry
          const yearKeys = Object.keys(companyData).filter(
            (key) =>
              key !== "sameCompanyKey" &&
              key !== "storageSystem" &&
              key !== "companyNameRef",
          );

          for (const yearKey of yearKeys) {
            const yearData = companyData[yearKey];
            if (!yearData || !yearData.Accounts) continue;

            const accountsRef = ref(
              database,
              `/User/${uid}/ClientCompanyAdded/${companyKey}/${yearKey}/Accounts`,
            );

            // Apply server-side date window when available
            let accountsQuery = accountsRef;
            const hasStart = !!startDate;
            const hasEnd = !!endDate;
            if (hasStart || hasEnd) {
              const parts = [orderByChild("date")];
              if (hasStart) parts.push(startAt(startDate));
              if (hasEnd) parts.push(endAt(endDate));
              accountsQuery = query(accountsRef, ...parts);
            }

            const accSnap = await get(accountsQuery);
            const accountsData = accSnap.val() || {};

            for (const key in accountsData) {
              allTransactions.push({
                id: key,
                ...accountsData[key],
                year:
                  yearData.yearEndDate || yearData.AmendedYearEnd || yearKey,
                companyKey,
                yearKey,
              });
            }
          }
        }

        console.log(
          "📊 Total transactions before filtering:",
          allTransactions.length,
        );

        // Client-side safety filter in case of inconsistent date strings
        if (startDate || endDate) {
          const start = parseComparableDate(startDate);
          const end = parseComparableDate(endDate);
          allTransactions = allTransactions.filter((transaction) => {
            if (!transaction.date) return true;
            const d = parseComparableDate(transaction.date);
            if (!d) return true;
            if (start && d < start) return false;
            if (end && d > end) return false;
            return true;
          });
        }

        // Filter out retained income entries only (keep journals)
        allTransactions = allTransactions.filter(
          (transaction) => transaction.date !== "RetainedIncomeForTB",
        );

        console.log(
          "✅ Final transactions after filtering:",
          allTransactions.length,
        );

        // Cache the result
        const cacheKey = makeCacheKey("allyears", {
          uid,
          companyId,
          storageSystem,
          startDate,
          endDate,
        });
        putInCache(cacheKey, allTransactions);
        return allTransactions;
      } catch (error) {
        console.error("Error loading legacy transactions:", error);
        throw error;
      }
    }
  } catch (error) {
    console.error("Error loading all years transactions:", error);
    throw error;
  }
};

/**
 * Hook for components to use the universal transaction loader
 */
export const useTransactions = (
  uid,
  companyId,
  yearSelected,
  dateFilters = {},
  enabled = true,
  knownStorageSystem = null,
) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { startDate, endDate } = dateFilters;

  useEffect(() => {
    if (!enabled || !uid || !companyId || !yearSelected) {
      setTransactions([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    const loadData = async () => {
      try {
        const data = await loadTransactions(
          uid,
          companyId,
          yearSelected,
          { startDate, endDate },
          knownStorageSystem,
        );
        console.log(
          "✅ useTransactions: Load complete, transactions:",
          data?.length || 0,
        );
        setTransactions(data);
      } catch (err) {
        console.error("❌ useTransactions: Error loading transactions:", err);
        setError(err);
      } finally {
        setLoading(false);
        console.log("🔚 useTransactions: Loading set to false");
      }
    };

    loadData();
  }, [
    uid,
    companyId,
    yearSelected,
    startDate,
    endDate,
    enabled,
    knownStorageSystem,
  ]);

  return { transactions, loading, error };
};

/**
 * Hook for components that need all years' transactions
 * Now uses TransactionContext for global state and real-time updates
 *
 * Benefits:
 * - Single listener per company (shared across all components)
 * - Instant navigation (data already loaded)
 * - Auto-sync across all report pages
 * - Client-side date filtering (instant, no network calls)
 *
 * @param {string} uid - Owner UID
 * @param {string} companyId - Company ID
 * @param {object} dateFilters - Optional { startDate, endDate, assessed } for client-side filtering
 *                                assessed: true (historical - assessed === true),
 *                                         false (new - assessed field missing OR assessed === false),
 *                                         null/undefined (all - for reports/other pages)
 * @param {boolean} enabled - Whether to subscribe to updates
 */
export const useAllYearsTransactions = (
  uid,
  companyId,
  dateFilters = {},
  enabled = true,
  options = {},
) => {
  const { subscribeToCompany, getTransactions, isLoading, getError } =
    useTransactionContext();

  // Destructure primitives to prevent object reference changes triggering re-renders
  const startDate = dateFilters?.startDate;
  const endDate = dateFilters?.endDate;
  const assessed = dateFilters?.assessed; // NEW: Extract assessed filter

  const { useFirestoreTotals = "auto", requireActualTransactions = false } = options;

  const [firestoreTransactions, setFirestoreTransactions] = useState([]);
  const [firestoreLoading, setFirestoreLoading] = useState(false);
  const [firestoreError, setFirestoreError] = useState(null);
  const [usingFirestore, setUsingFirestore] = useState(false);

  // Track last fetch to prevent duplicate reads
  const lastFetchRef = useRef(null);
  const firestoreReadCountRef = useRef(0);
  const [storageSystem, setStorageSystem] = useState(null);
  const [detectingStorage, setDetectingStorage] = useState(false);
  const lastFilteredTransactionsRef = useRef([]);

  // Detect storage system for auto mode
  useEffect(() => {
    if (useFirestoreTotals !== "auto" || !uid || !companyId) {
      setDetectingStorage(false);
      return;
    }

    let cancelled = false;
    const detectSystem = async () => {
      setDetectingStorage(true);
      try {
        const system = await detectStorageSystem(uid, companyId);
        if (cancelled) return;
        setStorageSystem(system);
        console.log(
          `📁 [useAllYearsTransactions] Storage system for ${companyId}: ${system}`,
        );
      } catch (error) {
        if (cancelled) return;
        console.error(
          `❌ [useAllYearsTransactions] Storage detection failed:`,
          error,
        );
        setStorageSystem("unified"); // Fallback to unified on error
      } finally {
        if (!cancelled) setDetectingStorage(false);
      }
    };

    detectSystem();
    return () => { cancelled = true; };
  }, [useFirestoreTotals, uid, companyId]);

  // Determine if we should try Firestore totals
  const shouldUseFirestore = useMemo(() => {
    if (useFirestoreTotals === true) return true; // Explicitly enabled
    if (useFirestoreTotals === false) return false; // Explicitly disabled
    // Auto mode: use Firestore if storage system is firestoreUnified
    return storageSystem === "firestoreUnified";
  }, [useFirestoreTotals, storageSystem]);

  // Attempt to load from Firestore totals if enabled
  useEffect(() => {
    if (!shouldUseFirestore || !enabled || !companyId) {
      setUsingFirestore(false);
      setFirestoreLoading(false);
      return;
    }

    let cancelled = false;
    const loadFirestoreTotals = async () => {
      // Create cache key from parameters
      const cacheKey = JSON.stringify({
        companyId,
        uid,
        assessed,
        requireActualTransactions,
        refreshKey: options.fileUploadKey || 0,
        startDate,
        endDate,
        period: endDate
          ? `${new Date(endDate).getFullYear()}-${(
              new Date(endDate).getMonth() + 1
            )
              .toString()
              .padStart(2, "0")}`
          : null,
      });

      // Check if we already fetched this exact data
      if (lastFetchRef.current === cacheKey) {
        setFirestoreLoading(false);
        console.log(
          `💾 [useAllYearsTransactions] Using cached data for ${companyId}, avoiding duplicate Firestore read`,
        );
        return;
      }

      console.log(
        `🔥 [useAllYearsTransactions] Fetching from Firestore (Read #${
          firestoreReadCountRef.current + 1
        }):`,
        { companyId, startDate, endDate },
      );

      setFirestoreLoading(true);
      setFirestoreError(null);

      try {
        // If we have an endDate, try to use totals (compatibility layer)
        // Transaction-level review needs source records, not account-total rows.
        if (endDate && !requireActualTransactions && assessed == null) {
          // Helper: Get all periods between start and end dates
          const getPeriodsInRange = async (start, end) => {
            const periods = [];
            const endDate = new Date(end);

            let startDate;

            if (start) {
              // Start date provided - use it
              startDate = new Date(start);
            } else {
              // No start date - find the earliest monthly total available
              try {
                const monthliesRef = collection(
                  db,
                  `companies/${companyId}/accountTotals_monthly`,
                );
                const snapshot = await getDocs(monthliesRef);

                if (!snapshot.empty) {
                  const earliestPeriod = snapshot.docs
                    .map((doc) => doc.id)
                    .sort()[0]; // Sort to get earliest (YYYY-MM format sorts correctly)

                  if (earliestPeriod) {
                    const [year, month] = earliestPeriod.split("-").map(Number);
                    startDate = new Date(year, month - 1, 1);
                    console.log(
                      `📊 [useAllYearsTransactions] No startDate provided, using earliest totals period: ${earliestPeriod}`,
                    );
                  }
                }
              } catch (error) {
                console.warn(
                  "⚠️ Could not find earliest period, using endDate:",
                  error,
                );
              }

              // Fallback: if no totals found, just use endDate (single period)
              if (!startDate) {
                startDate = new Date(
                  endDate.getFullYear(),
                  endDate.getMonth(),
                  1,
                );
              }
            }

            let current = new Date(
              startDate.getFullYear(),
              startDate.getMonth(),
              1,
            );

            while (current <= endDate) {
              const year = current.getFullYear();
              const month = current.getMonth() + 1;
              periods.push(`${year}-${month.toString().padStart(2, "0")}`);
              current.setMonth(current.getMonth() + 1);
            }

            return periods;
          };

          // Helper: Check if date range exactly matches ALL period boundaries
          const rangeMatchesAllPeriodBoundaries = (start, end, periods) => {
            if (!start || !end || periods.length === 0) {
              return false;
            }

            // Single period - validate start and end match exactly
            if (periods.length === 1) {
              const [periodYear, periodMonth] = periods[0]
                .split("-")
                .map(Number);
              const periodEnd = new Date(periodYear, periodMonth, 0); // Last day of month

              const filterStart = new Date(start);
              const filterEnd = new Date(end);

              // Check if filter range exactly matches period boundaries
              const startsMatch =
                filterStart.getFullYear() === periodYear &&
                filterStart.getMonth() === periodMonth - 1 &&
                filterStart.getDate() === 1;

              const endsMatch =
                filterEnd.getFullYear() === periodYear &&
                filterEnd.getMonth() === periodMonth - 1 &&
                filterEnd.getDate() === periodEnd.getDate();

              return startsMatch && endsMatch;
            }

            // Multi-period - validate FIRST and LAST periods match boundaries
            const firstPeriod = periods[0];
            const lastPeriod = periods[periods.length - 1];

            const [firstYear, firstMonth] = firstPeriod.split("-").map(Number);
            const [lastYear, lastMonth] = lastPeriod.split("-").map(Number);

            //   const firstDayOfStart = new Date(firstYear, firstMonth - 1, 1);
            const lastDayOfEnd = new Date(lastYear, lastMonth, 0);

            const filterStart = new Date(start);
            const filterEnd = new Date(end);

            const startMatches =
              filterStart.getFullYear() === firstYear &&
              filterStart.getMonth() === firstMonth - 1 &&
              filterStart.getDate() === 1;

            const endMatches =
              filterEnd.getFullYear() === lastYear &&
              filterEnd.getMonth() === lastMonth - 1 &&
              filterEnd.getDate() === lastDayOfEnd.getDate();

            // Both boundaries must match for multi-period totals to be valid
            return startMatches && endMatches;
          };

          // Get all periods in the date range
          const periods = await getPeriodsInRange(startDate, endDate);

          console.log(
            `🔥 [useAllYearsTransactions] Attempting Firestore totals for ${companyId}`,
            { periods, startDate, endDate, periodCount: periods.length },
          );

          // Validate that we can use totals for this range
          let canUseTotals = false;
          let validationReason = "";

          if (!startDate) {
            // A partial closing month must not include transactions after the cutoff.
            const end = parseComparableDate(endDate);
            const nextDay = end && new Date(end);
            if (nextDay) nextDay.setUTCDate(nextDay.getUTCDate() + 1);
            canUseTotals = Boolean(end && nextDay.getUTCMonth() !== end.getUTCMonth());
            validationReason = canUseTotals ? "Cumulative through month end" : "Partial closing month";
          } else {
            // With startDate - validate that range matches full period boundaries
            canUseTotals = rangeMatchesAllPeriodBoundaries(
              startDate,
              endDate,
              periods,
            );

            if (canUseTotals) {
              validationReason =
                periods.length === 1
                  ? "Single full period"
                  : `Multi-period (${periods.length} complete months)`;
            } else {
              validationReason =
                periods.length === 1
                  ? "Partial single period"
                  : "Partial multi-period range";
            }
          }

          if (!canUseTotals) {
            console.log(
              `⚠️ [useAllYearsTransactions] Date range doesn't match full period boundaries. Loading actual transactions for accurate filtering.`,
              {
                startDate,
                endDate,
                periods,
                reason: validationReason,
              },
            );
            // Fall through to load actual transactions
          } else {
            console.log(
              `✅ [useAllYearsTransactions] Using totals strategy: ${validationReason}`,
              {
                periods:
                  periods.length === 1
                    ? periods[0]
                    : `${periods[0]} to ${periods[periods.length - 1]}`,
              },
            );
            // Try to get totals for all required periods
            try {
              const totalsPromises = periods.map((period) =>
                firestoreAdapter.getAccountTotals(companyId, period),
              );

              const allTotals = await Promise.all(totalsPromises);
              const missingPeriods = periods.filter(
                (p, i) => !allTotals[i] || !allTotals[i].accounts,
              );

              if (missingPeriods.length > 0) {
                console.log(
                  `⚠️ [useAllYearsTransactions] Missing totals for periods: ${missingPeriods.join(
                    ", ",
                  )}. Loading actual transactions.`,
                );
                // Fall through to load actual transactions
              } else if (periods.length === 1) {
                // Single period - use existing logic
                const mockTransactions =
                  await firestoreAdapter.getTransactionsFromTotals(
                    companyId,
                    periods[0],
                  );

                if (mockTransactions && mockTransactions.length > 0) {
                  if (cancelled) return;
                  console.log(
                    `✅ [useAllYearsTransactions] Using Firestore totals: ${mockTransactions.length} accounts for period ${periods[0]} (1 Firestore read)`,
                  );
                  firestoreReadCountRef.current += 1;
                  lastFetchRef.current = cacheKey;
                  setFirestoreTransactions(mockTransactions);
                  setUsingFirestore(true);
                  setFirestoreLoading(false);
                  return;
                }
              } else {
                // Multi-period totals - merge them
                const mergedAccounts = {};

                allTotals.forEach((periodTotals, index) => {
                  if (!periodTotals?.accounts) return;

                  Object.entries(periodTotals.accounts).forEach(
                    ([accountName, data]) => {
                      if (!mergedAccounts[accountName]) {
                        mergedAccounts[accountName] = {
                          category: data.category,
                          balance: 0,
                          balanceVatExclusive: 0,
                          vatAmount: 0,
                          count: 0,
                        };
                      }

                      mergedAccounts[accountName].balance += data.balance || 0;
                      mergedAccounts[accountName].balanceVatExclusive +=
                        data.balanceVatExclusive || 0;
                      mergedAccounts[accountName].vatAmount +=
                        data.vatAmount || 0;
                      mergedAccounts[accountName].count += data.count || 0;
                    },
                  );
                });

                // Convert merged accounts to mock transactions
                const mockTransactions = Object.entries(mergedAccounts).map(
                  ([accountName, data]) => {
                    const endDateObj = new Date(endDate);
                    return {
                      Account_group: data.category,
                      account: accountName,
                      accountNames: accountName,
                      Opening_balance: data.balance,
                      Opening_balance_Vat_Exclusive: data.balanceVatExclusive,
                      vatAmount: data.vatAmount || 0,
                      date: endDate,
                      Date: endDate,
                      year: endDateObj.getFullYear().toString(),
                      _isMockTransaction: true,
                      _source: "firestore_totals_multi_period",
                      _periods: periods,
                      _transactionCount: data.count,
                    };
                  },
                );

                console.log(
                  `✅ [useAllYearsTransactions] Using merged Firestore totals: ${mockTransactions.length} accounts across ${periods.length} periods (${periods.length} Firestore reads)`,
                );
                if (cancelled) return;
                firestoreReadCountRef.current += periods.length;
                lastFetchRef.current = cacheKey;
                setFirestoreTransactions(mockTransactions);
                setUsingFirestore(true);
                setFirestoreLoading(false);
                return;
              }
            } catch (totalsError) {
              console.log(
                `⚠️ [useAllYearsTransactions] Error loading totals:`,
                totalsError.message,
              );
              // Fall through to load actual transactions
            }
          }
        }

        // Fallback: Load actual transactions from Firestore
        console.log(
          `🔥 [useAllYearsTransactions] Loading actual transactions from Firestore for ${companyId}`,
        );

        const actualTransactions = await firestoreAdapter.getTransactions(
          companyId,
          { startDate, endDate, assessed }, // NEW: Pass assessed filter
        );
        if (cancelled) return;

        if (actualTransactions && actualTransactions.length > 0) {
          console.log(
            `✅ [useAllYearsTransactions] Loaded ${actualTransactions.length} actual transactions from Firestore (${actualTransactions.length} Firestore reads)`,
          );
          firestoreReadCountRef.current += actualTransactions.length;
          lastFetchRef.current = cacheKey;
          // Mark transactions as actual (not from totals)
          const markedTransactions = actualTransactions.map((tx) => ({
            ...tx,
            _isActualTransaction: true,
            _source: "firestoreActual",
          }));
          setFirestoreTransactions(markedTransactions);
          setUsingFirestore(true);
        } else {
          console.log(
            `📊 [useAllYearsTransactions] No transactions in the selected Firestore period`,
          );
          lastFetchRef.current = cacheKey;
          setFirestoreTransactions([]);
          setUsingFirestore(true);
        }
        setFirestoreLoading(false);

        console.log(
          `📊 [useAllYearsTransactions] Total Firestore reads so far: ${firestoreReadCountRef.current}`,
        );
      } catch (err) {
        if (cancelled) return;
        console.warn(
          `⚠️ [useAllYearsTransactions] Firestore totals failed, falling back to regular transactions:`,
          err.message,
        );
        setFirestoreError(err.message);
        setUsingFirestore(false);
        setFirestoreLoading(false);
      }
    };

    loadFirestoreTotals();
    return () => { cancelled = true; };
  }, [
    shouldUseFirestore,
    enabled,
    uid,
    companyId,
    startDate,
    endDate,
    assessed,
    requireActualTransactions,
    options.fileUploadKey,
  ]);

  // Subscribe to real-time updates when component mounts (only if not using Firestore)
  useEffect(() => {
    if (usingFirestore) {
      // Skip real-time subscription if using Firestore totals
      return;
    }

    if (!enabled || !uid || !companyId) {
      return;
    }

    // Subscribe (will reuse existing listener if already subscribed)
    subscribeToCompany(uid, companyId);

    // Note: We don't unsubscribe here - listener stays active for other components
    // Cleanup happens at TransactionProvider level when user logs out or switches companies
  }, [uid, companyId, enabled, subscribeToCompany, usingFirestore]);

  // Get all transactions from context (only if not using Firestore)
  // Memoize to avoid changing reference on every render which can
  // cause dependent effects to re-run unnecessarily.
  const allTransactions = useMemo(() => {
    const txs = usingFirestore ? [] : getTransactions(companyId);
    if (txs.length > 0) {
      estimateDataSize(txs, `TransactionContext Data (${companyId})`);
    }
    return txs;
  }, [usingFirestore, companyId, getTransactions]);

  // Memoize loading and error to prevent unnecessary re-renders
  const loading = useMemo(
    () => (usingFirestore ? firestoreLoading : isLoading(companyId)),
    [usingFirestore, firestoreLoading, companyId, isLoading],
  );
  const error = useMemo(
    () => (usingFirestore ? firestoreError : getError(companyId)),
    [usingFirestore, firestoreError, companyId, getError],
  );

  // Feed presence into detection cache to avoid repeated unified presence reads
  useEffect(() => {
    if (!uid || !companyId) return;
    if (Array.isArray(allTransactions) && allTransactions.length > 0) {
      setUnifiedPresence(uid, companyId, true);
    }
  }, [uid, companyId, allTransactions]);
  // console.log("Transaction Debug - All Transactions:", companyId);

  // Client-side date filtering (memoized for performance)
  const filteredTransactions = useMemo(() => {
    // If using Firestore totals, return them directly (already filtered by period)
    if (usingFirestore) {
      const result = firestoreTransactions;
      // Only update ref if array actually changed (deep comparison for stability)
      if (
        result.length !== lastFilteredTransactionsRef.current.length ||
        result.some((tx, i) => tx !== lastFilteredTransactionsRef.current[i])
      ) {
        lastFilteredTransactionsRef.current = result;
      }
      return lastFilteredTransactionsRef.current;
    }

    // Otherwise filter regular transactions (RTDB context)
    let result = allTransactions;

    // Apply date filter
    if (startDate || endDate) {
      const start = parseComparableDate(startDate);
      const end = parseComparableDate(endDate);
      result = result.filter((transaction) => {
        if (!transaction || !transaction.date) return true;
        const transDate = parseComparableDate(transaction.date);
        if (!transDate) return true;
        if (start && transDate < start) return false;
        if (end && transDate > end) return false;
        return true;
      });
    }

    // NEW: Apply assessed filter for RTDB transactions from context
    // (Context doesn't support assessed filtering at query level)
    // assessed === true: Historical - only assessed transactions
    // assessed === false: New - transactions missing assessed field OR assessed === false
    if (assessed === true) {
      console.log(
        "📊 [Context Filter] Filtering for HISTORICAL (assessed === true)",
      );
      const beforeCount = result.length;
      result = result.filter((transaction) => transaction.assessed === true);
      console.log(
        `📊 [Context Filter] Filtered ${beforeCount} → ${result.length} assessed transactions`,
      );
    } else if (assessed === false) {
      console.log(
        "📊 [Context Filter] Filtering for NEW (assessed missing/false)",
      );
      const beforeCount = result.length;
      result = result.filter(
        (transaction) =>
          transaction.assessed === undefined ||
          transaction.assessed === null ||
          transaction.assessed === false,
      );
      console.log(
        `📊 [Context Filter] Filtered ${beforeCount} → ${result.length} unassessed transactions`,
      );
    } else {
      console.log(
        "📊 [Context Filter] No assessed filter - showing ALL transactions",
      );
    }
    // If assessed is null/undefined, return all (for reports/other pages)

    // Only update ref if array actually changed (shallow comparison for RTDB transactions)
    if (
      result.length !== lastFilteredTransactionsRef.current.length ||
      result.some((tx, index) => tx !== lastFilteredTransactionsRef.current[index])
    ) {
      lastFilteredTransactionsRef.current = result;
    }
    return lastFilteredTransactionsRef.current;
  }, [
    allTransactions,
    startDate,
    endDate,
    assessed, // NEW: Add assessed to dependencies
    usingFirestore,
    firestoreTransactions,
  ]);

  // console.log(
  //   `🔍 [useAllYearsTransactions] ${companyId}: ${filteredTransactions.length}/${allTransactions.length} transactions` +
  //     (startDate || endDate ? ` (filtered by date)` : "") +
  //     (usingFirestore ? " [FIRESTORE TOTALS]" : "")
  // );

  // console.log(
  //   `🔍 [useAllYearsTransactions] Returning ${filteredTransactions.length} transactions for ${companyId}` +
  //     (usingFirestore ? " [FIRESTORE]" : " [RTDB]") +
  //     (detectingStorage ? " [DETECTING...]" : "")
  // );

  return {
    transactions: filteredTransactions,
    // Raw, unfiltered transactions from TransactionContext (RTDB). Useful for calculations that
    // must not change based on assessed/date filters (e.g., bank balance on Accounts page).
    // For Firestore totals mode, fall back to the returned Firestore transactions.
    rawTransactions: usingFirestore ? firestoreTransactions : allTransactions,
    loading: loading || detectingStorage || firestoreLoading,
    error: shouldUseFirestore ? firestoreError : error,
    usingFirestore, // Expose whether Firestore is being used
    storageSystem, // Expose detected storage system
    detectingStorage, // Expose storage detection state
  };
};

/**
 * Save transactions to unified storage with correct property names
 */
export const saveUnifiedTransactions = async (uid, companyId, transactions) => {
  try {
    console.log(
      `💾 Saving ${transactions.length} transactions to unified storage for company: ${companyId}`,
    );

    const unifiedRef = database.ref(
      `/User/${uid}/UnifiedTransactions/${companyId}`,
    );
    const updates = {};

    transactions.forEach((transaction) => {
      // Ensure transaction has correct property names matching legacy system
      const formattedTransaction = {
        date: transaction.date,
        accountNames:
          transaction.accountNames ||
          transaction.Description ||
          transaction.description ||
          "",
        Account_group: transaction.Account_group || "No Account",
        Opening_balance: parseFloat(
          transaction.Opening_balance ||
            transaction.Amount ||
            transaction.amount ||
            0,
        ),
        vatType: transaction.vatType || "",
        vatAmount: parseFloat(transaction.vatAmount || 0),
        bankAccount: transaction.bankAccount || "",
        // Add any other properties that might be needed
        ...transaction,
      };

      const key = database.ref().push().key;
      updates[key] = formattedTransaction;
    });

    await unifiedRef.update(updates);

    // Mark company as using unified storage
    await markCompanyAsUnified(uid, companyId);
    console.log(
      `✅ Successfully saved ${transactions.length} transactions to unified storage`,
    );
    return { success: true, count: transactions.length };
  } catch (error) {
    console.error("❌ Error saving unified transactions:", error);
    throw error;
  }
};

/**
 * Universal transaction uploader that automatically detects system and saves accordingly
 */
export const saveTransactions = async (
  uid,
  companyId,
  transactions,
  yearSelected = null,
) => {
  try {
    const storageSystem = await detectStorageSystem(uid, companyId);

    if (storageSystem === "unified") {
      return await saveUnifiedTransactions(uid, companyId, transactions);
    } else {
      // For legacy system, we'd need to save to the year-based structure
      // This would require yearSelected parameter
      console.log(
        "⚠️ Legacy system detected - saving to year-based structure not implemented yet",
      );
      throw new Error("Legacy system saving not implemented");
    }
  } catch (error) {
    console.error("Error in universal transaction saver:", error);
    throw error;
  }
};

/**
 * 💰 Update Bank Closing Balance (Static Balance Maintenance)
 *
 * Atomically updates the closing balance for a bank account after transactions are added/edited.
 * This allows the UI to display balances instantly without summing all transactions.
 *
 * IMPORTANT: All transactions (bank feeds AND CSV uploads) store amounts in the
 * `Opening_balance` field. Bank feed APIs return an `amount` field, but it gets
 * saved as `Opening_balance` when written to the database.
 *
 * @param {string} uid - User ID
 * @param {string} companyId - Company ID (companyKEY)
 * @param {string} bankAccountId - Bank account ID
 * @param {number} amountChange - Net change to apply (positive for receipts, negative for payments)
 * @param {boolean} seed - If true, recalculates from scratch by summing all transactions (first-time setup)
 */
export const updateBankClosingBalance = async (
  uid,
  companyId,
  bankAccountId,
  amountChange = 0,
  seed = false,
  strict = false,
) => {
  try {
    if (!uid || !companyId || !bankAccountId) {
      if (strict) throw new Error("Bank balance destination is incomplete.");
      console.warn("⚠️ updateBankClosingBalance: Missing required parameters", {
        uid,
        companyId,
        bankAccountId,
      });
      return;
    }

    const bankAccountPath = `/bankAccounts/${uid}/bankingAccounts/${companyId}/Accounts/${bankAccountId}`;
    const bankAccountSnap = await get(ref(database, bankAccountPath));
    const bankAccountNode = bankAccountSnap.exists() ? bankAccountSnap.val() : null;
    const persistedBankId = String(bankAccountNode?.bankAccountAdded || "").trim();

    if (!bankAccountNode || !persistedBankId || persistedBankId !== bankAccountId) {
      if (strict) throw new Error("The destination bank account no longer exists or has an invalid ID.");
      console.warn(
        "⚠️ updateBankClosingBalance: Skipping invalid/non-existent bank account node",
        {
          uid,
          companyId,
          bankAccountId,
          exists: Boolean(bankAccountNode),
          persistedBankId,
        },
      );
      return;
    }

    const balancePath = `/bankAccounts/${uid}/bankingAccounts/${companyId}/Accounts/${bankAccountId}/bankClosingBalance`;
    const balanceRef = ref(database, balancePath);

    if (seed) {
      // SEED MODE: Calculate balance from scratch by summing all transactions from all sources
      console.log(
        `🌱 [Seed] Calculating closing balance for bank ${bankAccountId}...`,
      );

      let total = 0;
      let txCount = { unified: 0, legacy: 0, firestore: 0 };

      // Detect storage system first to avoid unnecessary queries
      const storageSystem = await detectStorageSystem(uid, companyId);
      console.log(
        `📁 [Seed] Detected storage system: ${storageSystem} for company ${companyId}`,
      );

      // Query transactions based on storage system
      if (storageSystem === "firestoreUnified") {
        // Firestore storage: Query Firestore transactions
        try {
          const {
            getFirestore,
            collection,
            query: fsQuery,
            where,
            getDocs,
          } = await import("firebase/firestore");
          const firestore = getFirestore();
          const firestoreTxQuery = fsQuery(
            collection(firestore, `companies/${companyId}/transactions`),
            where("bankAccount", "==", bankAccountId),
          );
          const firestoreTxSnapshot = await getDocs(firestoreTxQuery);

          if (!firestoreTxSnapshot.empty) {
            firestoreTxSnapshot.forEach((doc) => {
              const tx = doc.data();
              // Both bank feeds AND CSV uploads write to Opening_balance field
              const amount = Number(tx.Opening_balance) || 0;
              total += amount;
              txCount.firestore++;
            });
          }
          console.log(
            `🔥 [Seed] Firestore: Found ${txCount.firestore} transactions`,
          );
        } catch (firestoreErr) {
          if (strict) throw firestoreErr;
          console.error(`❌ [Seed] Firestore read failed:`, firestoreErr);
        }
      } else if (storageSystem === "unified") {
        // Unified RTDB storage: Query UnifiedTransactions only
        const unifiedRef = ref(
          database,
          `/User/${uid}/UnifiedTransactions/${companyId}`,
        );
        const unifiedQuery = query(
          unifiedRef,
          orderByChild("bankAccount"),
          equalTo(bankAccountId),
        );
        const unifiedSnap = await get(unifiedQuery);

        if (unifiedSnap.exists()) {
          const txns = unifiedSnap.val();
          Object.values(txns).forEach((tx) => {
            // Both bank feeds AND CSV uploads write to Opening_balance field
            const amount = Number(tx.Opening_balance) || 0;
            total += amount;
            txCount.unified++;
          });
        }
        console.log(`🌐 [Seed] Unified: Found ${txCount.unified} transactions`);
      } else if (strict) {
        // Imports route legacy records into year Accounts nodes. Read all sibling
        // years before replacing a cached balance; never replace it after a failed read.
        const companiesSnap = await get(ref(database, `/BusinessDetails/${uid}/ClientCompanyAdded`));
        const companies = companiesSnap.val() || {};
        const selected = companies[companyId];
        const nodeIds = new Set([companyId]);
        Object.entries(companies).forEach(([id, company]) => {
          if (selected?.sameCompanyKey && company.sameCompanyKey === selected.sameCompanyKey) nodeIds.add(id);
        });
        const seen = new Set();
        for (const nodeId of nodeIds) {
          const snapshot = await get(ref(database, `/User/${uid}/ClientCompanyAdded/${nodeId}`));
          const node = snapshot.val() || {};
          const buckets = [node.Transactions, ...Object.values(node).map(year => year?.Accounts)].filter(Boolean);
          for (const bucket of buckets) {
            for (const [id, tx] of Object.entries(bucket)) {
              if (seen.has(id) || String(tx.bankAccount || tx.bankAccountId || "") !== String(bankAccountId)) continue;
              seen.add(id);
              total += Number(tx.Opening_balance) || 0;
              txCount.legacy++;
            }
          }
        }
      } else {
        // Legacy RTDB storage: Query ClientCompanyAdded/Transactions
        try {
          const legacyRef = ref(
            database,
            `/User/${uid}/ClientCompanyAdded/${companyId}/Transactions`,
          );
          const legacyQuery = query(
            legacyRef,
            orderByChild("bankAccount"),
            equalTo(bankAccountId),
          );
          const legacySnap = await get(legacyQuery);

          if (legacySnap.exists()) {
            const txns = legacySnap.val();
            Object.values(txns).forEach((tx) => {
              // Both bank feeds AND CSV uploads write to Opening_balance field
              const amount = Number(tx.Opening_balance) || 0;
              total += amount;
              txCount.legacy++;
            });
          }
          console.log(`📚 [Seed] Legacy: Found ${txCount.legacy} transactions`);
        } catch (legacyErr) {
          console.error(`❌ [Seed] Legacy read failed:`, legacyErr);
        }
      }

      // Add the opening balance if exists
      const obPath = `/bankAccounts/${uid}/bankingAccounts/${companyId}/Accounts/${bankAccountId}/bankOpeningBalance`;
      const obSnap = await get(ref(database, obPath));
      const openingBalance = obSnap.exists() ? Number(obSnap.val()) || 0 : 0;
      total += openingBalance;

      await set(balanceRef, total);
      const totalTxCount = txCount.unified + txCount.legacy + txCount.firestore;
      console.log(
        `✅ [Seed] Set closing balance for ${bankAccountId}: ${total} ` +
          `(storage: ${storageSystem}, opening: ${openingBalance}, transactions: ${totalTxCount})`,
      );
      return total;
    } else {
      // INCREMENTAL MODE: Use atomic runTransaction to update balance
      const result = await runTransaction(balanceRef, (currentBalance) => {
        const current = Number(currentBalance) || 0;
        return current + Number(amountChange);
      });

      console.log(
        `💰 Updated balance for ${bankAccountId}: ${result.snapshot.val()} (delta: ${amountChange})`,
      );
      return result.snapshot.val();
    }
  } catch (error) {
    console.error("❌ Error updating bank closing balance:", error, {
      uid,
      companyId,
      bankAccountId,
      amountChange,
    });
    if (strict) throw error;
    // Don't throw - balance update is non-critical, transaction save should succeed
  }
};

/**
 * 🔄 Seed All Bank Balances for a Company
 *
 * Recalculates closing balances for all bank accounts from scratch.
 * Useful for first-time setup or when balances get out of sync.
 */
export const seedAllBankBalances = async (uid, companyId) => {
  try {
    console.log(`🌱 Seeding all bank balances for company ${companyId}...`);

    // Get all bank accounts for this company
    const bankAccountsRef = ref(
      database,
      `/bankAccounts/${uid}/bankingAccounts/${companyId}/Accounts`,
    );
    const bankSnap = await get(bankAccountsRef);

    if (!bankSnap.exists()) {
      console.log("ℹ️ No bank accounts found to seed");
      return;
    }

    const banks = bankSnap.val();
    const bankIds = Object.keys(banks);

    console.log(`🏦 Found ${bankIds.length} bank accounts to seed`);

    for (const bankId of bankIds) {
      await updateBankClosingBalance(uid, companyId, bankId, 0, true);
    }

    console.log(`✅ Successfully seeded ${bankIds.length} bank balances`);
  } catch (error) {
    console.error("❌ Error seeding bank balances:", error);
  }
};
